/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Modal, message, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { appLog } from '~src/utils/log';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import { deleteTag } from '~src/services/api';
import {
  deleteGroupLabel,
  Label,
  RegistrationLabel,
} from '~src/services/api/labels';

export interface DeleteModalProps extends Partial<ModalProps> {
  onDone?: () => void;
  group: RegistrationLabel | null;
  label: Label | null;
}

export const DeleteLabelModal: React.FC<DeleteModalProps> = ({
  onCancel,
  onDone,
  label,
  group,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();

  const [doDeleteTag, { isLoading }] = useMutation(deleteGroupLabel, {
    onSuccess: async (deletedLabel, {}) => {
      // hide modal
      // @ts-ignore
      onDone ? onDone() : onCancel && onCancel();
      queryCache.invalidateQueries('registrationLabelGroups');
    },
    onError: (error, item) => {
      queryCache.invalidateQueries('registrationLabelGroups');
      message.error(intl.formatMessage({ id: 'delete-failed' }));
      appLog('DeleteLabelModal -> deleteLabel -> items', item);
      appLog('DeleteLabelModal -> deleteLabel -> err', error);
    },
  });

  const handleSubmit = async () => {
    if (label?.id && group?.id) {
      doDeleteTag({
        projectId: projectId!,
        labelId: label?.id,
        groupId: group?.id,
      });
    }
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'delete' })}
          icon={['far', 'trash']}
        />
      }
      okText={intl.formatMessage({ id: 'delete' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ danger: true, loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      <Typography.Paragraph css={styles.text}>
        {intl.formatMessage(
          { id: 'delete-single-text' },
          {
            name: (label as Label)?.title,
          }
        )}
      </Typography.Paragraph>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  text: {
    textAlign: 'center',
    margin: 0,
    fontSize: 15,
  },
});
